import {
  ReactiveElement,
  ReactiveState
} from "@nrk/reactive-element";
import {
  version as playerCoreVersion
} from "@nrk/player-core/types";
import { guessMediaTypeOfUrl, toError, unlockMediaElement } from "@nrk/player-core/utils";
import { PSAPIClient } from "@nrk/player-psapi-client";
import { DimensionsMixin } from "@nrk/element-dimensions-mixin";
import { FocusVisibleMixin } from "@nrk/element-focus-visible-mixin";
import { FocusWithinController } from "@nrk/element-focus-within-controller";
import { getActionFromEvent, getFontSize } from "../utils/dom";
import iso8601 from "iso8601-duration";
import { ElementPanicMixin } from "../utils/element-panic-mixin";
import { PlayerPanicHandlerMixin } from "../utils/element-panic-handler-mixin";
import platform from "../utils/platform";
import { PlayerActions } from "./player-actions";
import { PlaybackStateMixin } from "./playback-state-mixin";
import { ShadowPropertiesMixin } from "@nrk/element-shadow-properties-mixin";
import { PlayerError, PlayerErrorTypes } from "./player-error";
import { durationToSeconds } from "../utils/duration";
import { EPGState } from "./epg-state";
import { PlayerEvents } from "./player-events";
import { getLogger } from "../utils/logging";
import { SubtitleState } from "./subtitles-state";
import { addPerformanceMark } from "../utils/performance";
const FONT_SIZE_BREAK_POINT = 500;
const PLAYER_ELEMENTS_VERSION = "6.0.0";
class PlayerElement extends DimensionsMixin(
  FocusVisibleMixin(
    PlaybackStateMixin(ShadowPropertiesMixin(PlayerPanicHandlerMixin(ElementPanicMixin(ReactiveElement))))
  )
) {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      blockedContentAge: Number,
      autoplay: Boolean,
      features: Array,
      loop: Boolean,
      preferredManifest: String,
      preferredSubtitle: String,
      preferredBandwidth: String,
      src: String,
      psapiOrigin: String,
      startTime: (element, propertyValue) => {
        if (!propertyValue || propertyValue.toString() === "undefined") {
          return;
        }
        if (isDate(propertyValue)) {
          element.startTimeLive = new Date(propertyValue);
        } else {
          element.startTimeOndemand = Number(propertyValue);
        }
      },
      cdnOverride: String
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      epgState: ReactiveState,
      interactionPointState: Object,
      metadata: Object,
      poster: String,
      resolvedSrc: String,
      resolvedType: String,
      speak: String,
      focusWithin: Boolean,
      theaterMode: Boolean,
      subtitlesHidden: Boolean
    };
  }
  interactionPointState;
  metadata;
  poster = "";
  startupSkipPointDelay = 0;
  resolvedSrc;
  resolvedType;
  speak = "";
  autoplay = false;
  epgState;
  features = [];
  focusWithin = false;
  focusWithinController;
  cdnOverride;
  preferredManifest = void 0;
  preferredSubtitle = void 0;
  preferredBandwidth;
  isAwaitingPlaybackStart = false;
  isBinging = false;
  isSuspended = false;
  pendingMetadataResponse;
  pendingMetadataPlayable;
  psapiClient;
  psapiOrigin = "https://psapi.nrk.no";
  player;
  playerName = "";
  playerVersionNumber = "n/a";
  playerFamily = "player-core";
  loop = false;
  startTimeOndemand;
  startTimeLive;
  src = "";
  user;
  theaterMode;
  subtitlesHidden = false;
  savedPlaybackState;
  _speakClearTimeoutId = 0;
  _countdownIntervalId = 0;
  logger = getLogger("Player");
  constructor() {
    super();
    this.interactionPointHandler = this.interactionPointHandler.bind(this);
    this.getAuthToken = this.getAuthToken.bind(this);
    this.focusWithinController = new FocusWithinController(this, {
      onChanged: () => {
        this.focusWithin = this.focusWithinController.value;
        this.classList.toggle("focus-within", this.focusWithin);
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("change", this);
    this.addEventListener("click", this);
    this.addEventListener("input", this);
  }
  readyCallback() {
    if (!this.src) {
      const source = getSourceFromElement(this);
      if (source) {
        const { src, type } = source;
        this.resolvedType = type;
        this.src = src;
      }
    }
    if (platform.isMobile) {
      this.classList.add("mobile");
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroySession();
    this.psapiClient?.abort();
    this.psapiClient = void 0;
    this.removeEventListener("click", this);
    this.removeEventListener("input", this);
    this.removeEventListener("change", this);
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "volume") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "muted") {
      this.activeSession?.[this.muted ? "mute" : "unmute"]();
    } else if (propName === "activeSession") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "metadata" && this.metadata) {
      this.isLive = this.metadata.isLive ?? false;
      if (this.preferredManifest !== void 0) {
        const preferredManifest = this.metadata.accessabilityVersions?.find((a) => a.type === this.preferredManifest);
        if (preferredManifest && !this.src.match(preferredManifest.id)) {
          this.src = `nrk:program:${preferredManifest.id}`;
          this.pendingMetadataPlayable = void 0;
        }
      }
    } else if (propName === "manifest" && this.manifest && "playable" in this.manifest) {
      const source = getSourceFromManifest(this.manifest);
      if (source) {
        this.resolvedSrc = source.src;
        this.resolvedType = source.type;
      }
    } else if (propName === "src") {
      const isSrcReset = oldValue !== void 0;
      if (isSrcReset) {
        this.manifest = void 0;
        this.metadata = void 0;
        this.psapiClient?.abort();
        this.psapiClient = void 0;
        this.resolvedSrc = void 0;
        this.isSuspended = false;
        if (this.savedPlaybackState) {
          this.startTimeOndemand = this.savedPlaybackState.startTime;
        } else {
          this.startTimeOndemand = void 0;
        }
        this.stop();
      }
      if (this.src && this.src !== "undefined") {
        if (PSAPIClient.isSupported(this.src)) {
          const options = {
            origin: this.psapiOrigin,
            playerFamily: this.playerFamily
          };
          if (this.user?.id) {
            options.enableEeaPortability = true;
            options.authTokenFunction = this.getAuthToken;
            options.contentGroup = this.user?.contentGroup;
          }
          options.ageRestriction = this.user?.ageRestriction;
          if (this.cdnOverride) {
            options.cdnOverride = this.cdnOverride;
          }
          if (this.preferredBandwidth) {
            options.preferredBandwidth = this.preferredBandwidth;
          }
          this.psapiClient = new PSAPIClient(this.src, this.playerName, options);
          this.loadMetadata();
        } else {
          this.resolvedSrc = this.src;
        }
      }
    } else if (propName === "isRealtime") {
      this.isRealtime ? this.classList.add("realtime") : this.classList.remove("realtime");
    } else if (propName === "isLive") {
      this.isLive ? this.classList.add("live") : this.classList.remove("live");
    } else if (propName === "isEnded" && this.isEnded) {
      this.startTimeOndemand = void 0;
    } else if (propName === "playbackRate") {
      this.activeSession?.setPlaybackRate(this.playbackRate);
    } else if (propName === "dimensions" && this.dimensions) {
      this.adjustFontSize();
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    const isChangedSrc = changedProperties.has("src");
    const isValidSrc = this.src && this.src !== "undefined";
    const shouldSuspend = (this.activeSession !== void 0 || this.isAwaitingPlaybackStart) && changedProperties.has("hidden") && this.hidden;
    const shouldResume = changedProperties.has("hidden") && !this.hidden && !isChangedSrc && isValidSrc;
    const shouldPlay = isChangedSrc && isValidSrc && (changedProperties.get("isPlaying") === true || this.autoplay);
    if (isChangedSrc) {
      this.interactionPointState = void 0;
    }
    if (shouldSuspend) {
      this.suspend();
    } else if (shouldResume) {
      this.resume();
    } else if (shouldPlay) {
      this.play();
    }
    if (changedProperties.has("isStarted") && this.isStarted) {
      addPerformanceMark("playback-startup-time-end");
    }
  }
  handleEvent(event) {
    if (event.defaultPrevented) {
      return;
    }
    const [action, value] = getActionFromEvent(event);
    if (!action) {
      return;
    }
    switch (action) {
      case PlayerActions.EnterFullscreen:
        event.preventDefault();
        this.activeSession?.enterFullscreen();
        break;
      case PlayerActions.ExitFullscreen:
        event.preventDefault();
        this.activeSession?.exitFullscreen();
        break;
      case PlayerActions.EnterPip:
        event.preventDefault();
        this.hideSubtitles(true);
        if (platform.isSafari) {
          this.activeSession?.enableNativeSubtitlesDisplay();
        }
        this.activeSession?.enterPiP();
        break;
      case PlayerActions.ExitPip:
        event.preventDefault();
        if (platform.isSafari) {
          this.activeSession?.disableNativeSubtitlesDisplay();
        }
        this.hideSubtitles(false);
        this.activeSession?.exitPiP();
        break;
      case PlayerActions.EnterTheaterMode:
        event.preventDefault();
        this.theaterMode = true;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.EnterTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.ExitTheaterMode:
        event.preventDefault();
        this.theaterMode = false;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.ExitTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.EnterRemotePlayback:
        event.preventDefault();
        this.activeSession?.enterRemotePlayback();
        break;
      case PlayerActions.Play:
        event.preventDefault();
        this.play();
        break;
      case PlayerActions.Pause:
        event.preventDefault();
        this.activeSession?.pause();
        break;
      case PlayerActions.TogglePlay:
        event.preventDefault();
        this.togglePlay();
        break;
      case PlayerActions.Mute:
        event.preventDefault();
        this.muted = true;
        break;
      case PlayerActions.Unmute:
        event.preventDefault();
        this.muted = false;
        break;
      case PlayerActions.StopPlayNextCountdown:
        this.interactionPointState = void 0;
        window.clearInterval(this._countdownIntervalId);
        break;
      case PlayerActions.PlayNextEpisode:
        this.abortInteractionPoint();
        this.playNext();
        break;
      case PlayerActions.ToggleSubtitles: {
        event.preventDefault();
        this.subtitleState?.toggleVisibility();
        break;
      }
      case PlayerActions.ToggleMute: {
        event.preventDefault();
        this.toggleMute();
        break;
      }
      case PlayerActions.SelectStream: {
        if (value !== this.src) {
          this.savedPlaybackState = { startTime: this.activeSession?.getCurrentTime() };
          this.src = value;
          this.dispatchEvent(
            new CustomEvent(PlayerEvents.StreamChange, {
              bubbles: true,
              cancelable: true,
              detail: { accessibilityVersion: this.manifestTypeFromSrc() }
            })
          );
        }
        break;
      }
      case PlayerActions.SetPoster: {
        event.preventDefault();
        if (value) {
          this.poster = value;
        }
        break;
      }
      case PlayerActions.SetVolume: {
        event.preventDefault();
        if (value !== void 0) {
          const parsedValue = typeof value === "string" ? parseFloat(value) : value;
          this.volume = Math.min(Math.max(parsedValue / 100, 0), 1);
        }
        break;
      }
      case PlayerActions.SeekTo: {
        event.preventDefault();
        this.interactionPointState = void 0;
        if (value !== void 0) {
          this.seekTo(value);
        }
        break;
      }
      case PlayerActions.SeekToLive: {
        event.preventDefault();
        this.activeSession?.seekToLive();
        break;
      }
      case PlayerActions.SeekToRelativeTime: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToRelativeTime(value);
        }
        break;
      }
      case PlayerActions.SeekToPosition: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToPosition(value);
        }
        break;
      }
      case PlayerActions.SkipToPoint: {
        this.seekTo(value);
        break;
      }
      case PlayerActions.Speak: {
        event.preventDefault();
        if (value) {
          this.speak = value;
          clearTimeout(this._speakClearTimeoutId);
          this._speakClearTimeoutId = window.setTimeout(() => {
            this.speak = "";
          }, 2e3);
        }
        break;
      }
    }
  }
  get currentTime() {
    return this.activeSession?.getCurrentTime() ?? 0;
  }
  hideSubtitles(hidden) {
    this.subtitlesHidden = hidden;
  }
  preload(options) {
    return this.activeSession?.preload(options);
  }
  async play() {
    if (this.isAwaitingPlaybackStart) {
      return;
    }
    if (this.isSuspended) {
      return this.resume();
    }
    this.isWaiting = !this.player || !this.activeSession;
    if (!this.player) {
      try {
        await this.initPlayerCoreInstance();
      } catch (err) {
        this.isWaiting = false;
        this.panic(err);
        return;
      }
    }
    if (!this.activeSession) {
      this.isAwaitingPlaybackStart = true;
      this.unlockAudioVideoElement();
      if (PSAPIClient.isSupported(this.src)) {
        await this.loadMetadata();
      }
      if (!this.resolvedSrc) {
        await this.loadManifest(this.manifestTypeFromSrc());
        addPerformanceMark("playback-startup-time-start");
      }
      if (this.resolvedSrc) {
        await this.createSession();
      }
      this.isAwaitingPlaybackStart = false;
      if (!this.activeSession || this.isSuspended) {
        this.isWaiting = false;
        this.stop();
        return;
      }
    }
    try {
      await this.activeSession.play();
    } catch (err) {
      this.isWaiting = false;
      if (toError(err).name !== "AutoplayError") {
        this.panic(err);
      }
    }
  }
  pause() {
    if (this.activeSession?.isPlaying()) {
      return this.activeSession.pause();
    }
  }
  togglePlay() {
    if (!this.activeSession || !this.activeSession.isPlaying()) {
      return this.play();
    }
    this.pause();
  }
  async suspend(replayOnResume) {
    this.isSuspended = true;
    if (this.activeSession) {
      this.savedPlaybackState = {
        isPlaying: this.isEnded || this.isPlaying || replayOnResume,
        startTime: this.isStarted ? this.currentTime : this.startTimeOndemand
      };
    }
    return this.stop();
  }
  async resume() {
    this.isSuspended = false;
    if (this.savedPlaybackState) {
      const savedPlaybackState = this.savedPlaybackState;
      this.startTimeOndemand = savedPlaybackState.startTime;
      this.savedPlaybackState = void 0;
      await this.play();
      if (!savedPlaybackState.isPlaying) {
        this.pause();
        this.isStarted = true;
      }
    } else {
      this.play();
    }
  }
  stop() {
    this.isAwaitingPlaybackStart = false;
    this.psapiClient?.abort();
    return this.destroySession();
  }
  toggleMute() {
    this.muted = !this.muted;
  }
  resetPlaybackRate() {
    this.activeSession?.resetPlaybackRate();
  }
  seekTo(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekTo(time);
  }
  seekToRelativeTime(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekToRelativeTime(time);
  }
  seekToPosition(position) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof position === "string") {
      position = parseFloat(position);
    }
    const range = this.activeSession.getSeekableRange();
    const time = position * (range.end - range.start) + range.start;
    this.activeSession.seekTo(time);
  }
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: false, detail }));
  }
  interactionPointHandler(event) {
    const eventData = event.range.data;
    if (event.type === "enter") {
      if (eventData.autoskipInBingeMode && this.isBinging) {
        this.seekTo(eventData.seekToTime);
        this.isBinging = false;
        return;
      }
      this.interactionPointState = {
        type: eventData.type,
        visible: true,
        displayValue: eventData.displayValue,
        seekToTime: eventData.seekToTime
      };
      if (eventData.type === "nextUpPoint") {
        this.isBinging = true;
        this.interactionPointState = {
          countdownDuration: eventData.countdownDuration,
          seekToTime: eventData.seekToTime,
          visible: true,
          type: eventData.type,
          displayValue: eventData.displayValue,
          originalCountdownDuration: eventData.countdownDuration
        };
        this._countdownIntervalId = window.setInterval(() => {
          if (this.interactionPointState?.countdownDuration) {
            this.interactionPointState = {
              ...this.interactionPointState,
              countdownDuration: this.interactionPointState.countdownDuration - 1
            };
          }
          if (this.interactionPointState?.countdownDuration === 0) {
            window.clearInterval(this._countdownIntervalId);
          }
        }, 1e3);
      }
    } else if (event.type === "exit") {
      if (this.interactionPointState?.countdownDuration) {
        this.abortInteractionPoint();
        return this.playNext();
      }
      if (!this.interactionPointState?.manualExtension || this.interactionPointState?.manualExtension && this.currentTime < event.range.start) {
        this.interactionPointState = void 0;
        this.isBinging = false;
      }
    }
  }
  get updateComplete() {
    return new Promise((resolve) => {
      super.updateComplete.then(() => {
        return this.pendingMetadataResponse;
      }).then(() => {
        resolve(this.isUpdatePending);
      });
    });
  }
  async _onMediaInfoLoaded(event) {
    super._onMediaInfoLoaded(event);
    this.addEventsForInteractionPoints();
  }
  handlePanic(error) {
    super.handlePanic(error);
    this.suspend();
  }
  canPlayType(mediaType) {
    throw Error('PlayerElement subclass must implement "canPlayType()" method based on required PlayerCore adapters');
  }
  playerVersion() {
    return `${this.playerName}@${this.playerVersionNumber} (player-elements@${PLAYER_ELEMENTS_VERSION}/player-core@${playerCoreVersion})`;
  }
  abortInteractionPoint() {
    this.interactionPointState = void 0;
    window.clearInterval(this._countdownIntervalId);
  }
  resetNextUpCountdown() {
    if (this.interactionPointState) {
      this.interactionPointState.countdownDuration = this.interactionPointState.originalCountdownDuration;
    }
  }
  abortNextUpCountdown() {
    if (this.interactionPointState) {
      window.clearInterval(this._countdownIntervalId);
      this.interactionPointState.countdownDuration = void 0;
      this.interactionPointState.displayValue = "Neste";
      this.interactionPointState.manualExtension = true;
      this.isBinging = false;
    }
  }
  async initPlayerCoreInstance() {
    throw Error('PlayerElement subclass must implement "initPlayerCoreInstance()" method');
  }
  async createSession() {
    await this.destroySession();
    if (!this.resolvedSrc) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.MISSING_SOURCE,
          true,
          new Error("Avspilling feilet p\xE5 grunn av manglende mediakilde")
        )
      );
    }
    const mediaType = (this.resolvedType ? { type: this.resolvedType } : guessMediaTypeOfUrl(this.resolvedSrc)) ?? {
      type: "video/mp4"
    };
    if (!this.canPlayType(mediaType)) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.SOURCE_NOT_SUPPORTED,
          true,
          new Error("Avspilling av mediainnholdet er ikke st\xF8ttet p\xE5 denne enheten"),
          { mediaId: this.src, mediaUrl: this.resolvedSrc }
        )
      );
    }
    try {
      const initialState = {
        mute: this.muted,
        startTime: this.metadata?.isChannel ? void 0 : this.startTimeOndemand,
        startTimeLive: this.metadata?.isChannel ? this.startTimeLive : void 0,
        videoPoster: this.poster
      };
      const session = this.player.createPlaybackSession(this.resolvedSrc, mediaType, initialState);
      this._observePlaybackSession(session);
      if (this.metadata?.isChannel) {
        this.epgState = new EPGState(this);
      }
    } catch (err) {
      this.panic(err);
    }
  }
  destroySession() {
    if (this.activeSession) {
      const session = this.activeSession;
      this._unobservePlaybackSession();
      this.epgState?.destroy();
      this.epgState = void 0;
      return session.close();
    }
    return Promise.resolve();
  }
  async loadMetadata() {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load metadata for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.metadata) {
      return;
    }
    if (this.pendingMetadataPlayable) {
      return this.pendingMetadataPlayable;
    }
    const iterator = this.psapiClient.loadMetadata({ throwNonPlayableError: true });
    let resolvePlayability;
    this.pendingMetadataPlayable = new Promise((resolve) => resolvePlayability = resolve);
    const iterate = () => {
      this.pendingMetadataResponse = iterator.next().then((result) => {
        this.pendingMetadataResponse = void 0;
        if (!result.done) {
          this.metadata = parseMetadata(result.value, this.startTimeOndemand);
          iterate();
        } else {
          resolvePlayability();
          this.pendingMetadataPlayable = void 0;
        }
      }).catch((err) => {
        this.pendingMetadataPlayable = void 0;
        this.pendingMetadataResponse = void 0;
        if (toError(err).name !== "AbortError") {
          this.panic(err);
        }
      });
    };
    iterate();
    return this.pendingMetadataPlayable;
  }
  async loadManifest(manifestType) {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load manifest for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.pendingMetadataPlayable) {
      await this.pendingMetadataPlayable;
    }
    try {
      const manifestResponse = await this.psapiClient.updateManifest(manifestType);
      this.manifest = parseManifest(manifestResponse.data);
    } catch (err) {
      if (toError(err).name !== "AbortError") {
        this.panic(err);
      }
    }
  }
  unlockAudioVideoElement() {
    const element = this.querySelector("video,audio");
    if (element) {
      unlockMediaElement(element);
    }
  }
  adjustFontSize() {
    this.style.setProperty("--player-base-font-size", this.getRemFontSize());
  }
  getRemFontSize() {
    const scale = this.dimensions.width < FONT_SIZE_BREAK_POINT ? 2 : 5 / 4;
    const absoluteSize = scale * this.baseFontSize();
    const relativeSize = absoluteSize / getFontSize(document.documentElement);
    return `${relativeSize}rem`;
  }
  baseFontSize() {
    const maxAspectRatio = 1 / 0.5625;
    const referenceSize = getFontSize(document.documentElement) / 800;
    const baseWidth = Math.min(this.dimensions.width, this.dimensions.height * maxAspectRatio);
    return baseWidth * referenceSize;
  }
  manifestTypeFromSrc() {
    if (this.src.match(/TOLK/)) {
      return "tegntolk";
    } else if (this.src.match(/SYNS/)) {
      return "synstolk";
    }
    return "default";
  }
  addEventsForInteractionPoints() {
    if (this.metadata?.interactionPoints?.seekToPoints && this.activeSession) {
      const timeRangeEvents = this.activeSession.createTimeRangeEventTrack();
      for (const seekToPoint of this.metadata.interactionPoints.seekToPoints) {
        this.logger.log("adding TimeRangeEvent for seekToPoint", seekToPoint);
        const startTime = seekToPoint.startTime === 0 ? this.startupSkipPointDelay : seekToPoint.startTime;
        const range = timeRangeEvents?.addRange(
          { start: startTime, end: seekToPoint.endTime },
          {
            type: "seekToPoint",
            displayValue: seekToPoint.displayValue,
            seekToTime: seekToPoint.seekToTime,
            autoskipInBingeMode: seekToPoint.autoSkipInBingeMode
          }
        );
        range?.on("enter", this.interactionPointHandler);
        range?.on("exit", this.interactionPointHandler);
      }
      if (this.metadata?.interactionPoints?.nextUpPoint && this.metadata.nextSrc) {
        const nextUpPoint = this.metadata.interactionPoints.nextUpPoint;
        this.logger.info("adding TimeRangeEvent for nextUpPoint", nextUpPoint);
        const range = timeRangeEvents?.addRange(
          { start: nextUpPoint.startTime, end: this.metadata.duration },
          {
            type: "nextUpPoint",
            displayValue: "Neste om",
            countdownDuration: nextUpPoint.countDownDuration
          }
        );
        range?.on("enter", this.interactionPointHandler);
        range?.on("exit", this.interactionPointHandler);
      }
    }
  }
  playNext() {
    if (this.metadata?.nextSrc) {
      this.dispatchEvent(
        new CustomEvent(PlayerEvents.PlayNextEpisode, {
          bubbles: true,
          cancelable: true,
          detail: {
            src: this.metadata?.nextSrc
          }
        })
      );
      this.interactionPointState = void 0;
      this.isBinging = true;
    }
  }
  getAuthToken() {
    if (this.user?.id) {
      return this.user.getAuthToken().then((token) => {
        return Promise.resolve(token);
      }).catch((error) => {
        return Promise.resolve(void 0);
      });
    }
    return Promise.resolve(void 0);
  }
  fakeError(type, fatal = false) {
    this.dispatchEvent(
      new CustomEvent(PlayerEvents.PlayerError, {
        bubbles: true,
        cancelable: true,
        detail: new PlayerError(
          type,
          fatal,
          type ? new Error(`FakeError ${type}`) : new Error(
            `fakeError(errorType: string | PlayerErrorTypes, fatal = false)
Throw an error out of the player to any listening context. Error types can be:
- ${Object.keys(
              PlayerErrorTypes
            ).join("\n- ")}
`
          )
        )
      })
    );
  }
}
function parseMetadata(metadata, startTime) {
  const images = metadata.preplay.poster.images;
  const available = metadata.playability === "playable";
  const availability = parseAvailability(metadata);
  let posterSrcset = images.reduce((srcset, { url, pixelWidth }, i) => {
    srcset += `${url} ${pixelWidth}w, `;
    return srcset;
  }, "");
  const accessabilityVersions = metadata._embedded.manifests?.map((m) => {
    return { id: m.id, label: m.availabilityLabel, type: m._links.self.name };
  });
  if (images[0]?.url) {
    posterSrcset += `${images[0].url}`;
  }
  const nextSrc = parseNext(metadata);
  return {
    availability: available ? { available: true } : {
      available: false,
      ...availability
    },
    description: metadata.preplay.description,
    duration: metadata.duration ? iso8601.toSeconds(iso8601.parse(metadata.duration)) : 0,
    heading: metadata.preplay.titles.title,
    id: metadata.id,
    isLive: metadata.streamingMode === "live",
    isChannel: metadata.availability.live?.type === "channel",
    legalAge: metadata.legalAge,
    startTime,
    posterSrcset,
    nextSrc,
    subheading: metadata.preplay.titles.subtitle,
    standaloneProgram: metadata._links.series || metadata.availability.live?.type === "channel" ? false : true,
    interactionPoints: metadata.interactionPoints,
    indexPoints: parseIndexPoints(metadata.preplay.indexPoints),
    accessabilityVersions
  };
}
function parseNext(metadata) {
  if (!metadata._embedded.next) {
    return void 0;
  }
  const url = metadata._embedded.next._links.self.href;
  const id = url.substring(url.lastIndexOf("/") + 1);
  return `nrk:program:${id}`;
}
function parseIndexPoints(indexPoints) {
  return indexPoints.map((point) => {
    return { title: point.title, startPoint: durationToSeconds(point.startPoint) };
  });
}
function parseAvailability(metadata) {
  const { live, onDemand } = metadata.availability || {};
  const availability = {};
  if (live && live.transmissionInterval) {
    availability.from = new Date(live.transmissionInterval.from);
    availability.to = new Date(live.transmissionInterval.to);
  }
  if (onDemand) {
    availability.from = new Date(onDemand.from);
    availability.to = new Date(onDemand.to);
  }
  if (metadata.nonPlayable) {
    availability.reason = metadata.nonPlayable.reason;
    availability.endUserMessage = metadata.nonPlayable.endUserMessage;
    availability.messageType = metadata.nonPlayable.messageType;
  }
  availability.isPending = availability.from ? Date.now() < availability.from.getTime() : false;
  return availability;
}
function parseManifest(manifest) {
  if (manifest.playable) {
    const assetUrl = manifest.playable.assets[0].url;
    const baseUrl = assetUrl.substring(0, assetUrl.lastIndexOf("/"));
    const thumbnails = manifest.playable.thumbnails && manifest.playable.thumbnails.length > 0 ? manifest.playable.thumbnails : [
      { url: `${baseUrl}/thumbnails.vtt?width=320&height=180`, width: 320 },
      { url: `${baseUrl}/thumbnails.vtt?width=480&height=270`, width: 480 }
    ];
    return {
      statistics: manifest.statistics,
      playable: manifest.playable,
      thumbnails
    };
  }
}
function getSourceFromManifest(manifest) {
  const assets = manifest.playable.assets;
  if (assets && assets.length) {
    const asset = assets[0];
    return { src: asset.url, type: asset.mimeType };
  }
}
function getSourceFromElement(element) {
  for (const { src, type } of Array.from(element.querySelectorAll("source"))) {
    if (element.canPlayType({ type })) {
      return { src, type };
    }
  }
}
function isDate(value) {
  return isNaN(Number(value));
}
export {
  PLAYER_ELEMENTS_VERSION,
  PlayerActions,
  PlayerElement,
  PlayerErrorTypes,
  SubtitleState
};
